/* eslint-disable no-mixed-spaces-and-tabs */
import { createSelector } from 'reselect';
import isEmpty from 'lodash/isEmpty';
import moment from 'moment';
import { getFormValues, isInvalid } from 'redux-form';
import { initialState } from './reducer';
import { DATE_FORMAT, DATE_TIME_FORMAT, FORM_NAME, ORDER_TABLE_ROWS_NAMES } from './constants';
import { thousandSeparator } from '../reqs-filter/utils';
import { formatContactPhone } from '../reqs-details-modal/utils';
import { getLocalStorageItem } from '../../utils/localStorage';

export const selectOrdersModalDomain = state => state.ordersModalReducer || initialState;
export const selectRouterDomain = state => state.router || {};

export const getOrderInfoSelector = () =>
	createSelector(selectOrdersModalDomain, ({ orderInfo }) => {
		if (!orderInfo) {
			return [];
		}
		const {
			customer,
			number,
			created_date,
			points,
			pickup_at,
			drop_at,
			mode,
			weight,
			volume,
			place,
			cost,
			attr_group,
			attr_way,
			comment,
		} = orderInfo;

		const addressFrom = points.find(({ type }) => type === 'from') || {};

		const addressTo = points.find(({ type }) => type === 'to') || {};

		const data = {
			information_group: { group: 'Информация о заказе' },
			customer: {
				name: customer?.name || '-',
				code: customer?.inn || '-',
				sub_value: {
					name: customer?.contact?.name,
					code: formatContactPhone(customer?.contact?.phone, customer?.contact?.extension),
				},
			},
			number: number || '---',
			created_date: created_date ? moment(created_date).format(DATE_FORMAT) : '---',
			customer_group: { group: 'Грузоотправитель' },
			address_from: {
				name: addressFrom.partner?.name,
				inn: addressFrom.partner?.inn,
				address: addressFrom.address?.address || '-',
				time: addressFrom.address?.work_mode || '-',
				sub_value: {
					name: addressFrom.contact?.name || '-',
					code: formatContactPhone(addressFrom?.contact?.phone, addressFrom?.contact?.extension),
				},
			},
			pickup_at: pickup_at ? moment(pickup_at).format(DATE_TIME_FORMAT) : '---',
			mode: mode?.name ? `${mode.name} ${mode.id !== 1 ? '°C' : ''}` : 'Отсутствует',
			cargo: {
				weight: weight ? thousandSeparator(weight) : '',
				volume: volume ? thousandSeparator(volume) : '',
				place: place ? thousandSeparator(place) : '',
			},
			cost: cost ? `${thousandSeparator(cost)} руб` : '---',
			attr_group: attr_group || '---',
			attr_way: attr_way || '---',
			comment: comment || '---',
			consignee_group: { group: 'Грузополучатель' },
			address_to: {
				name: addressTo.partner?.name,
				inn: addressTo.partner?.inn,
				address: addressTo.address?.address || '-',
				time: addressTo.address?.work_mode || '-',
				cargo_date: addressTo.date ? moment(addressTo.date).format(DATE_FORMAT) : '-',
				sub_value: {
					name: addressTo.contact?.name || '-',
					code: formatContactPhone(addressTo?.contact?.phone, addressTo?.contact?.extension),
				},
			},
			drop_at: drop_at ? moment(drop_at).format(DATE_TIME_FORMAT) : '---',
		};

		return Object.entries(data).reduce((result, [key, value]) => {
			if (value.group) {
				result.push(value);
			} else {
				result.push({
					id: key,
					title: ORDER_TABLE_ROWS_NAMES[key],
					value,
					sub_value: value.sub_value ? value.sub_value : '',
				});
			}

			return result;
		}, []);
	});

export const getIsOrderInfoLoadingSelector = () =>
	createSelector(selectOrdersModalDomain, ({ isOrderInfoLoading }) => isOrderInfoLoading);

export const getIsOrderInfoSendingSelector = () =>
	createSelector(selectOrdersModalDomain, ({ isOrderDeleting, isOrderSaving }) => isOrderDeleting || isOrderSaving);

export const getOrderNameSelector = () =>
	createSelector(selectOrdersModalDomain, ({ orderInfo }) => {
		if (!orderInfo) return '';

		return `Заказ #${orderInfo?.id || '-'} от ${
			orderInfo?.created_at ? moment(orderInfo.created_at).format(DATE_TIME_FORMAT) : '-'
		}`;
	});

export const getIsModalShownSelector = () =>
	createSelector(selectRouterDomain, ({ location: { query } }) => {
		return Boolean(!isEmpty(query) && query?.modal === 'order');
	});

export const getIsAuditTabShownSelector = () =>
	createSelector(selectRouterDomain, ({ location: { query } }) => {
		return Boolean(!isEmpty(query) && query?.modal === 'order' && query?.tab === 'audit');
	});

export const getActiveOrderIdSelector = () =>
	createSelector(selectRouterDomain, ({ location: { query } }) => (query?.modal === 'order' ? query?.id : null));

export const getIsOrderAddFormOpenSelector = () =>
	createSelector(selectRouterDomain, ({ location: { query } }) => {
		return Boolean(!isEmpty(query) && query?.modal === 'order' && query?.mode === 'add');
	});

export const getIsOrderEditFormOpenSelector = () =>
	createSelector(selectRouterDomain, ({ location: { query } }) => {
		return Boolean(!isEmpty(query) && query?.modal === 'order' && Boolean(query?.id) && query?.mode === 'edit');
	});

export const getCustomersSelector = () => createSelector(selectOrdersModalDomain, ({ customers }) => customers);

export const getIsCustomersLoadingSelector = () =>
	createSelector(selectOrdersModalDomain, ({ isCustomersLoading }) => isCustomersLoading);

export const getPartnersSelector = () => createSelector(selectOrdersModalDomain, ({ partners }) => partners);

export const getPartnerSearchValueSelector = () =>
	createSelector(selectOrdersModalDomain, ({ partnerSearchValue }) => partnerSearchValue);

export const getIsPartnersLoadingSelector = () =>
	createSelector(selectOrdersModalDomain, ({ isPartnersLoading }) => isPartnersLoading);

export const getPartnersOptionsSelector = () =>
	createSelector(selectOrdersModalDomain, ({ partners }) => {
		return Object.values(partners).reduce((acc, partner, index) => {
			acc[index] = partner.map(item => ({
				...item,
				key: item.id,
				value: item.id,
				text: item.name,
			}));

			return acc;
		}, {});
	});

export const getFormInitialValuesSelector = () =>
	createSelector(selectOrdersModalDomain, getFormValues(FORM_NAME), ({ orderInfo }) => {
		if (!orderInfo) {
			const companyId = getLocalStorageItem('user_data').companyId;

			return {
				customer_id: companyId,
			};
		}

		return {
			...orderInfo,
			customer_id: orderInfo?.customer?.id,
		};
	});

export const getPointsSelector = () =>
	createSelector(getFormInitialValuesSelector(), formValues => {
		return formValues.points || [{}, {}];
	});

export const getAddressesOptionsSelector = () =>
	createSelector(selectOrdersModalDomain, ({ addresses }) => {
		return Object.values(addresses).reduce((acc, partner, index) => {
			acc[index] = partner.map(item => ({
				...item,
				key: item.id,
				value: item.id,
				text: item.name,
			}));

			return acc;
		}, {});
	});

export const getAddressSearchValueSelector = () =>
	createSelector(selectOrdersModalDomain, ({ addressSearchValue }) => addressSearchValue);

export const getAddressesSelector = () => createSelector(selectOrdersModalDomain, ({ addresses }) => addresses);

export const getContactsSelector = () => createSelector(selectOrdersModalDomain, ({ contacts }) => contacts);

export const getContactsOptionsSelector = () =>
	createSelector(selectOrdersModalDomain, ({ contacts }) => {
		return Object.values(contacts).reduce((acc, partner, index) => {
			acc[index] = partner.map(item => ({
				...item,
				key: item.id,
				value: item.id,
				text: item.name,
			}));

			return acc;
		}, {});
	});

export const getContactSearchValueSelector = () =>
	createSelector(selectOrdersModalDomain, ({ contactSearchValue }) => contactSearchValue);

export const getModesSelector = () => createSelector(selectOrdersModalDomain, ({ modes }) => modes);

export const getFormIsInvalidSelector = () => createSelector(isInvalid(FORM_NAME), isInvalid => isInvalid);

export const getIsAddressesLoadingSelector = () =>
	createSelector(selectOrdersModalDomain, ({ isAddressesLoading }) => isAddressesLoading);

export const getIsContactsLoadingSelector = () =>
	createSelector(selectOrdersModalDomain, ({ isContactsLoading }) => isContactsLoading);

export const getIsModesLoadingSelector = () =>
	createSelector(selectOrdersModalDomain, ({ isModesLoading }) => isModesLoading);

export const getDataForSaveSelector = () =>
	createSelector(getFormValues(FORM_NAME), formValues => {
		return {
			customer_id: formValues.customer_id,
			number: formValues.number || null,
			created_date: formValues.created_date || null,
			points: formValues.points?.map((point, index) => {
				return {
					partner_id: point.partner?.id,
					address_id: point.address?.id,
					contact_id: point.contact?.id,
					...(index === 1 ? { date: point.date } : {}),
				};
			}),
			mode_id: formValues.mode?.id || null,
			weight: formValues.weight,
			volume: formValues.volume,
			place: formValues.place || null,
			cost: formValues.cost || null,
			attr_group: formValues.attr_group || null,
			attr_way: formValues.attr_way || null,
			comment: formValues.comment || null,
		};
	});

export const getFormValueSelector = () =>
	createSelector(getFormValues(FORM_NAME), formValues => {
		return formValues;
	});

export const getOrderTabsSelector = () =>
	createSelector(selectOrdersModalDomain, ({ orderInfo }) => orderInfo?.tabs || {});

export const getOrderActionsSelector = () =>
	createSelector(selectOrdersModalDomain, ({ orderInfo }) => orderInfo?.actions || {});
